<template>
  <!-- Component Badge đa năng: Tự render <span> hoặc <router-link> tuỳ thuộc có prop "to" hay không -->
  <component
    v-if="isVisible"
    :is="computedTo ? 'router-link' : 'span'"
    :to="computedTo"
    :target="computedTo ? target : undefined"
    :style="computedStyle"
    :class="['custom-badge', computedVariantClass, { 'is-link': !!computedTo }]"
    v-b-tooltip.hover
    :title="computedTitle || undefined"
  >
    <!-- Ưu tiên nội dung slot, nếu không có thì lấy computedText -->
    <slot>{{ computedText }}</slot>
  </component>
</template>

<style lang="scss" scoped>
// Style chung cho tất cả các badge
.custom-badge {
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 11px;
  display: inline-block;
  text-transform: none;
  vertical-align: middle;
  line-height: 1.2;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  text-decoration: none;

  // Trạng thái hover khi badge là đường link
  &.is-link {
    cursor: pointer;

    &:hover {
      text-decoration: none;
      opacity: 0.9;
    }
  }

  // Biến thể đỏ nhạt: Dùng cho tag cuộc họp gốc (sourceMeetingId)
  &.variant-light-danger {
    background-color: #fee2e2;
    color: #dc3545;
    border: 1px solid rgba(220, 53, 69, 0.2);

    &.is-link:hover {
      background-color: #fecaca;
      color: #bd2130;
      opacity: 1;
    }
  }
}
</style>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { getColorByText, hexToRgba, getCoreDomain } from "@/service/util";

/**
 * Component Badge dùng chung cho toàn ứng dụng:
 * 1. Tag cuộc họp gốc: truyền :meeting="item" (tự động lấy clonedMeetingId / sourceMeetingId)
 * 2. Tag domain hạ tầng: truyền :domain="item.coreDomain"
 * 3. Badge tuỳ biến thông thường: truyền text, variant, to, customStyle,...
 */
@Component
export default class Badge extends Vue {
  // Object cuộc họp (tự động lấy clonedMeetingId hoặc sourceMeetingId)
  @Prop({ default: undefined }) readonly meeting!: Record<string, any> | null | undefined;

  // Domain hạ tầng core (nếu có sẽ tự tính mã màu pastel và tên domain rút gọn)
  @Prop({ default: undefined }) readonly domain!: string | null | undefined;

  // Đường dẫn link điều hướng (nếu có thì badge sẽ render dạng <router-link>)
  @Prop({ default: null }) readonly to!: string | null;

  // Thẻ target khi click mở link (mặc định mở tab mới "_blank")
  @Prop({ default: "_blank" }) readonly target!: string;

  // Nội dung chữ hiển thị (nếu không dùng slot)
  @Prop({ default: null }) readonly text!: string | number | null;

  // Tiêu đề tooltip khi hover
  @Prop({ default: null }) readonly title!: string | null;
  @Prop({ default: null }) readonly tooltip!: string | null;

  // Biến thể màu sắc (ví dụ: 'light-danger')
  @Prop({ default: "" }) readonly variant!: string;

  // Style CSS tuỳ biến truyền thêm vào thẻ
  @Prop({ default: () => ({}) }) readonly customStyle!: Record<string, any>;

  /**
   * Lấy ID cuộc họp liên quan từ object `meeting` (clonedMeetingId)
   */
  get computedMeetingId(): number | string | null | undefined {
    return this.meeting?.clonedMeetingId ?? null;
  }

  /**
   * Kiểm tra điều kiện hiển thị badge:
   * - Nếu truyền meeting: ẩn khi ID cuộc họp liên quan là null, undefined hoặc rỗng.
   * - Nếu truyền domain: ẩn khi giá trị là null, undefined hoặc rỗng.
   */
  get isVisible(): boolean {
    if (this.meeting !== undefined) {
      const srcId = this.computedMeetingId;
      return srcId !== null && srcId !== undefined && srcId !== "";
    }
    if (this.domain !== undefined) {
      return this.domain !== null && this.domain !== "";
    }
    return true;
  }

  /**
   * Xác định đường dẫn điều hướng:
   * - Ưu tiên prop `to` do người dùng truyền vào.
   * - Nếu có ID cuộc họp liên quan: tự động tạo link đến trang chi tiết cuộc họp.
   */
  get computedTo(): string | null {
    if (this.to) {
      return this.to;
    }
    const srcId = this.computedMeetingId;
    if (srcId != null && srcId !== "") {
      return `/meetings/details/${srcId}`;
    }
    return null;
  }

  /**
   * Xác định tooltip khi hover:
   * - Ưu tiên prop `title` hoặc `tooltip`.
   * - Với cuộc họp liên quan: hiển thị "Cuộc họp gốc: #ID" từ đa ngữ.
   * - Với domain: hiển thị tên đầy đủ của domain core.
   */
  get computedTitle(): string {
    if (this.title) return this.title;
    if (this.tooltip) return this.tooltip;
    const srcId = this.computedMeetingId;
    if (srcId != null && srcId !== "") {
      return this.$t("sourceMeetingTooltip", { id: srcId }).toString();
    }
    if (this.domain) {
      return this.domain;
    }
    return "";
  }

  /**
   * Xác định nội dung chữ hiển thị bên trong badge:
   * - Ưu tiên prop `text`.
   * - Với cuộc họp liên quan: hiển thị format "#ID".
   * - Với domain: hiển thị domain rút gọn qua hàm `getCoreDomain`.
   */
  get computedText(): string | number {
    if (this.text != null) return this.text;
    const srcId = this.computedMeetingId;
    if (srcId != null && srcId !== "") {
      return `#${srcId}`;
    }
    if (this.domain) {
      return getCoreDomain(this.domain);
    }
    return "";
  }

  /**
   * Xác định class biến thể màu sắc CSS:
   * - Ưu tiên prop `variant`.
   * - Với cuộc họp liên quan: tự động áp dụng biến thể đỏ nhạt `variant-light-danger`.
   */
  get computedVariantClass(): string {
    if (this.variant) {
      return `variant-${this.variant}`;
    }
    const srcId = this.computedMeetingId;
    if (srcId != null && srcId !== "") {
      return "variant-light-danger";
    }
    return "";
  }

  /**
   * Tính toán style màu động (inline style):
   * - Với domain: tự động sinh màu chữ ngẫu nhiên theo hash chuỗi (`getColorByText`)
   *   và màu nền rgba có độ trong suốt 0.1 (`hexToRgba`).
   */
  get computedStyle(): Record<string, any> {
    const style: Record<string, any> = { ...this.customStyle };
    if (this.domain) {
      const color = getColorByText(this.domain);
      style.backgroundColor = hexToRgba(color, 0.1);
      style.color = color;
    }
    return style;
  }
}
</script>
